
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import {
  addMonths,
  addWeeks,
  addYears,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  isSameDay,
  startOfToday,
  endOfToday,
  startOfTomorrow,
  endOfTomorrow,
  addDays,
  subDays,
} from 'date-fns';
import { isDescendant } from '../../helpers/dom';
import CalendarIcon from 'ah-common-lib/src/icons/components/CalendarIcon.vue';
import { getFieldErrorList, makeFormModel } from 'ah-common-lib/src/form/helpers';
import { FieldError, FormEvent } from 'ah-common-lib/src/form/interfaces';
import { singleInputDateField } from 'ah-common-lib/src/form/models';
import { FormModel } from 'ah-common-lib/src/form/interfaces';
import { date, ifTest, minDate, maxDate } from 'ah-common-lib/src/form/validators';
import { Validation } from '@vuelidate/core';
import { cloneDeep, uniqueId, isEmpty } from 'lodash';
import { getChildModel } from 'ah-common-lib/src/form/helpers';
import { FieldModel } from 'ah-common-lib/src/form/interfaces';

export interface DateFilterActions extends DateRange {
  label: string;
}

export interface DateRange {
  start: Date;
  end: Date;
}

export const defaultChoices = [
  { label: 'Today', start: startOfToday(), end: endOfToday() },
  { label: 'Tomorrow', start: startOfTomorrow(), end: endOfTomorrow() },
  { label: 'This week', start: startOfWeek(new Date()), end: endOfWeek(new Date()) },
  { label: 'Next week', start: startOfWeek(addWeeks(new Date(), 1)), end: endOfWeek(addWeeks(new Date(), 1)) },
  { label: 'This month', start: startOfMonth(new Date()), end: endOfMonth(new Date()) },
  { label: 'Next month', start: startOfMonth(addMonths(new Date(), 1)), end: endOfMonth(addMonths(new Date(), 1)) },
  { label: 'This year', start: startOfYear(new Date()), end: endOfYear(new Date()) },
  { label: 'Next year', start: startOfYear(addYears(new Date(), 1)), end: endOfYear(addYears(new Date(), 1)) },
];

/**
 * Range date selector
 *
 * Emits:
 * - date (payload: DateRange): .sync'able date range
 * - submit (payload: Date | DateRange): date selected on submittion
 */
@Component({
  components: {
    DatePicker,
    CalendarIcon,
  },
})
export default class InputDateSelector extends Vue {
  $refs!: {
    popover: Vue;
    datePicker: Vue & {
      move: (arg: number | Date | string | { month: number; year: number }, opts: any) => Promise<void>;
    };
  };

  /**
   * Title of the Date selector
   */
  @Prop({ default: '' }) title!: string;

  /**
   * Confirm button label
   */
  @Prop({ default: 'Confirm' }) confirmButtonLabel!: string;

  /**
   * Cancel button label
   */
  @Prop({ default: 'Cancel' }) cancelButtonLabel!: string;

  /**
   * Whether date picker is a range of dates
   */
  @Prop({ default: false }) isRanged!: boolean | string;

  /**
   * Only relevant if picker is a range of dates
   *
   * If set, when selecting a date, if the other value is undefined, will automatically select a single day date range
   */
  @Prop({ default: false }) setSingleDateByDefault!: boolean | string;

  /**
   * Known date ranges that the user can select to auto fill the date picker
   *
   * if not ranged date this will be ignored
   */
  @Prop({ default: () => defaultChoices }) commonChoices!: DateFilterActions[];

  /**
   * Date selected in the date picker
   */
  @Prop({ default: null }) date!: DateRange | Date | null;

  /**
   * Default option selected
   */
  @Prop({ default: null }) defaultDate!: DateRange | Date | null;

  /**
   * Dates not able to be selected
   */
  @Prop({ default: null }) excludedDates!: Date[] | null;

  /**
   * Whether clear option is available
   */
  @Prop({ default: false }) clearable!: boolean | string;

  /**
   * Clear value
   *
   * Will be ignored if prop `clearable` is set to false
   */
  @Prop({ default: null }) clearValue!: DateRange | Date | null;

  /**
   * Additional changes to the calendar options
   * Same values for selected attribute and dragable attribute is being used
   */
  @Prop({ required: false }) options?: any[];

  /**
   * Whether to display action choices
   */
  @Prop({ default: false }) hideChoices?: string | boolean;

  /**
   * Whether to display action choices
   */
  @Prop({ default: false }) setDateOnInvalid?: string | boolean;

  private show = false;

  private fromPage: { month: number; year: number } = { month: 0, year: 0 };

  private toPage: { month: number; year: number } = { month: 0, year: 0 };

  private dateFromForm: FormModel | null = null;

  private dateToForm: FormModel | null = null;

  private selectedDate: DateRange | Date | null = null;

  private dateToSelect: DateRange | Date | null = null;

  private dateFromValidation: Validation | null = null;

  private dateToValidation: Validation | null = null;

  private popOverTarget = `date-menu-toggle-${uniqueId()}`;

  windowClickListener!: (event: MouseEvent) => void;

  created() {
    this.dateFromForm = makeFormModel({
      name: 'dateFrom',
      fieldType: 'form',
      fields: [
        singleInputDateField(
          'date',
          '',
          {
            required: false,
            showCalendar: false,
            fieldWrapperClass: 'col col-12 mb-0',
            useLocalTime: 'true',
            errorMessages: {
              maxDate: 'Invalid Start date',
              date: 'Must be a valid date',
            },
          },
          {
            maxDate: ifTest(
              maxDate(() => this.end ?? undefined),
              () => this.end !== null
            ),
            date: ifTest(date, (val) => val instanceof Date),
          }
        ),
      ],
    });

    this.dateToForm = makeFormModel({
      name: 'dateTo',
      fieldType: 'form',
      fields: [
        singleInputDateField(
          'date',
          '',
          {
            required: false,
            showCalendar: false,
            fieldWrapperClass: 'col col-12 mb-0',
            useLocalTime: 'true',
            errorMessages: {
              minDate: 'Invalid End date',
              date: 'Must be a valid date',
            },
          },
          {
            minDate: ifTest(
              minDate(() => this.start ?? undefined),
              () => this.start !== null
            ),
            date: ifTest(date, (val) => val instanceof Date),
          }
        ),
      ],
    });

    this.onDateChange();
    this.onSelectedDateChange();
  }

  mounted() {
    this.windowClickListener = (event: any) => {
      if (this.show && this.$refs.popover && !isDescendant(this.$refs.popover.$el, event.target)) {
        this.show = false;
      }
    };
    window.addEventListener('click', this.windowClickListener);
  }

  beforeDestroy() {
    window.removeEventListener('click', this.windowClickListener);
  }

  onClickShowButton() {
    if (this.show === false) {
      setTimeout(() => {
        if (!this.dateToSelect) {
          this.dateToSelect = cloneDeep(this.defaultDate);
        }
        this.show = true;
      });
    }
  }

  private get noChoices() {
    return this.hideChoices !== false;
  }

  private get attributes() {
    return {
      highlight: {
        class: 'date-highlight',
        contentClass: 'date-highlight-content',
      },
      ...this.options,
    };
  }

  get start() {
    if (!this.selectedDate) {
      return null;
    }

    if (this.selectedDate instanceof Date) {
      return this.selectedDate;
    } else if (typeof this.selectedDate === 'string') {
      return new Date(this.selectedDate);
    } else {
      if (typeof (this.selectedDate as DateRange).start === 'string') {
        return new Date(this.selectedDate.start);
      }
      return (this.selectedDate as DateRange).start;
    }
  }

  get end() {
    if (!this.selectedDate) {
      return null;
    }

    if (this.selectedDate instanceof Date) {
      return this.selectedDate;
    } else if (typeof this.selectedDate === 'string') {
      return new Date(this.selectedDate);
    } else {
      if (typeof (this.selectedDate as DateRange).end === 'string') {
        return new Date(this.selectedDate.end);
      }
      return (this.selectedDate as DateRange).end;
    }
  }

  get isRangedDatePicker() {
    return this.isRanged !== false;
  }

  get isDirty() {
    if (!this.selectedDate) return false;
    if (!isNaN(new Date(this.selectedDate as Date).getDate())) {
      return !isSameDay(this.selectedDate as Date, this.clearValue as Date);
    } else if (this.isRangedDatePicker) {
      const range = this.selectedDate as DateRange;
      const defaultRange = this.clearValue as DateRange;
      return (
        (!defaultRange && !!this.selectedDate) ||
        !(isSameDay(defaultRange.start, range.start) && isSameDay(defaultRange.end, range.end))
      );
    }

    return this.selectedDate !== this.clearValue;
  }

  get isInvalid() {
    return this.fieldErrors.length > 0;
  }

  get shouldSetDateOnInvalid() {
    return this.setDateOnInvalid !== false;
  }

  get fieldErrors(): FieldError[] {
    if (!this.dateFromForm || !this.dateToForm || !this.dateFromValidation || !this.dateToValidation) return [];

    return [
      ...getFieldErrorList(this.dateFromValidation!, getChildModel(this.dateFromForm, 'date')! as FieldModel),
      ...getFieldErrorList(this.dateToValidation!, getChildModel(this.dateToForm, 'date')! as FieldModel),
    ];
  }

  isActive(choice: DateFilterActions) {
    if (this.isRangedDatePicker && this.dateToSelect) {
      const range = this.dateToSelect as DateRange;
      return isSameDay(choice.start, new Date(range.start)) && isSameDay(choice.end, new Date(range.end));
    }
    return false;
  }

  clear() {
    this.dateToSelect = this.clearValue;
    this.selectedDate = this.clearValue;
    this.$emit('update:date', this.selectedDate);
  }

  cancel() {
    this.show = false;
  }

  /**
   * Select date/date range from one of the choices provided.
   *
   * If current view does not include any of the dates being selected, it will auto scroll to the beginning of the range
   */
  selectFromChoice(date: DateRange | Date) {
    this.dateToSelect = date;
    const minPageDate = new Date(this.fromPage.year, this.fromPage.month - 1, 1);
    const maxPageDate = endOfMonth(new Date(this.toPage.year, this.toPage.month - 1, 1));
    const minDate = !(date instanceof Date) ? (date as DateRange).start : (date as Date);
    const maxDate = !(date instanceof Date) ? (date as DateRange).end : (date as Date);
    if (!maxDate || !minDate || minPageDate > maxDate || maxPageDate < minDate) {
      this.$refs.datePicker.move({ month: minDate!.getMonth() + 1, year: minDate!.getFullYear() }, { position: 1 });
    }
  }

  onStartDateAdded(event: FormEvent) {
    if (this.dateFromValidation?.$invalid) {
      if (this.shouldSetDateOnInvalid) {
        this.dateToSelect = this.date;
      } else {
        return;
      }
    } else if (event.event === 'form-field-set-value') {
      const dateChoice = this.dateFromForm!.date?.toString().length > 0 ? new Date(this.dateFromForm!.date) : null;
      if (!this.isRangedDatePicker) {
        if (this.shouldSetDateOnInvalid) {
          this.dateToSelect = dateChoice ?? this.date;
        } else {
          this.dateToSelect = dateChoice;
        }
      } else if (this.dateFromForm!.date.toString().length > 0) {
        (this.dateToSelect as any) = { ...this.dateToSelect, start: dateChoice };
        if (dateChoice && this.setSingleDateByDefault !== false && !(this.dateToSelect as DateRange).end) {
          (this.dateToSelect as DateRange).end = addDays(dateChoice, 1);
        }
      }
    }
    this.submit();
  }

  onEndDateAdded(event: FormEvent) {
    if (this.dateToValidation?.$invalid) return;

    if (event.event === 'form-field-set-value') {
      const dateChoice = this.dateToForm!.date?.toString().length > 0 ? new Date(this.dateToForm!.date) : null;
      if (this.dateToForm!.date.toString().length > 0) {
        (this.dateToSelect as any) = { ...this.dateToSelect, end: dateChoice };
        if (dateChoice && this.setSingleDateByDefault !== false && !(this.dateToSelect as DateRange).start) {
          (this.dateToSelect as DateRange).start = subDays(dateChoice, 1);
        }
      }
      this.submit();
    }
  }

  submit() {
    this.show = false;
    this.selectedDate = !isEmpty(this.dateToSelect) ? this.dateToSelect : null;
    if (this.dateToSelect instanceof Date && this.dateFromForm) {
      this.dateFromForm.date = this.dateToSelect;
    } else if (this.dateFromForm && this.dateToForm) {
      this.dateFromForm.date = (this.dateToSelect as DateRange)?.start;
      this.dateToForm.date = (this.dateToSelect as DateRange)?.end;
    }

    this.$emit('update:date', this.selectedDate);
  }

  @Watch('date')
  onDateChange() {
    this.selectedDate = this.date;
    if (this.dateFromForm) {
      this.dateFromForm.date = this.start;
    }
    if (this.dateToForm) {
      this.dateToForm.date = this.end;
    }
  }

  @Watch('selectedDate')
  onSelectedDateChange() {
    this.dateToSelect = this.selectedDate;
  }
}
